import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { X } from "lucide-react";
import Button from "../common/Button";
import Input from "../common/Input";
import Modal from "../common/Modal";
import RichTextEditor from "../common/RichTextEditor";
import { appealService } from "../../services";
import { validations } from "../../utils/validations";

const EditAppealModal = ({
  isOpen,
  appeal,
  onClose,
  onSuccess,
  loading: parentLoading = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [editingSectionIndex, setEditingSectionIndex] = useState(null);
  const [formData, setFormData] = useState({
    appeal: "",
    slug: "",
    selectedImage: null,
    existingImage: null,
    titleEn: "",
    titleBn: "",
    sections: [],
    sectionType: "heading",
    sectionContentEn: "",
    sectionContentBn: "",
    sectionOrder: 1,
    sectionImage: null,
    sectionImagePreview: null,
  });

  // Load appeal data when modal opens
  useEffect(() => {
    if (isOpen && appeal) {
      setFormData({
        appeal: appeal.appeal || "",
        slug: appeal.slug || "",
        selectedImage: null,
        existingImage: appeal.image || null,
        titleEn: appeal.title?.en || "",
        titleBn: appeal.title?.bn || "",
        sections: appeal.sections
          ? appeal.sections.map((section) => ({
              ...section,
              imagePreview: section.image || null,
              imageFile: null,
              hasImage: Boolean(section.image),
            }))
          : [],
        sectionType: "heading",
        sectionContentEn: "",
        sectionContentBn: "",
        sectionOrder: 1,
        sectionImage: null,
        sectionImagePreview: null,
      });
      setEditingSectionIndex(null);
    }
  }, [isOpen, appeal]);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (file) {
      if (!validations.isValidFile(file)) {
        toast.error("Invalid file. Please upload an image (max 10MB)");
        setFormData({ ...formData, selectedImage: null });
        return;
      }
      setFormData({ ...formData, selectedImage: file });
    }
  };

  const handleSectionImageChange = (e) => {
    const file = e.target.files?.[0];

    if (file) {
      if (!validations.isValidFile(file)) {
        toast.error("Invalid file. Please upload an image (max 10MB)");
        setFormData({
          ...formData,
          sectionImage: null,
          sectionImagePreview: null,
        });
        return;
      }

      setFormData({
        ...formData,
        sectionImage: file,
        sectionImagePreview: URL.createObjectURL(file),
      });
    }
  };

  const clearSectionImage = () => {
    setFormData({
      ...formData,
      sectionImage: null,
      sectionImagePreview: null,
    });
  };

  const addSection = () => {
    if (
      !formData.sectionContentEn.trim() ||
      !formData.sectionContentBn.trim()
    ) {
      toast.error("Please fill both English and Bengali content");
      return;
    }

    const newSection = {
      type: formData.sectionType,
      content: {
        en: formData.sectionContentEn,
        bn: formData.sectionContentBn,
      },
      order: formData.sectionOrder,
      image: null,
      cloudinary_public_id: null,
      imageFile: formData.sectionImage,
      imagePreview: formData.sectionImagePreview,
      hasImage: Boolean(formData.sectionImage),
    };

    setFormData({
      ...formData,
      sections: [...formData.sections, newSection],
      sectionContentEn: "",
      sectionContentBn: "",
      sectionOrder: formData.sectionOrder + 1,
      sectionImage: null,
      sectionImagePreview: null,
    });
  };

  const removeSection = (index) => {
    const updated = formData.sections.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      sections: updated,
    });
  };

  const startEditSection = (index) => {
    const section = formData.sections[index];
    setFormData({
      ...formData,
      sectionType: section.type,
      sectionContentEn: section.content.en,
      sectionContentBn: section.content.bn,
      sectionOrder: section.order,
      sectionImage: null,
      sectionImagePreview: section.image || section.imagePreview || null,
    });
    setEditingSectionIndex(index);
  };

  const saveEditSection = () => {
    if (
      !formData.sectionContentEn.trim() ||
      !formData.sectionContentBn.trim()
    ) {
      toast.error("Please fill both English and Bengali content");
      return;
    }

    const updated = [...formData.sections];
    const keepSectionImage = formData.sectionType === "paragraph";
    updated[editingSectionIndex] = {
      ...updated[editingSectionIndex],
      type: formData.sectionType,
      content: {
        en: formData.sectionContentEn,
        bn: formData.sectionContentBn,
      },
      order: formData.sectionOrder,
      imageFile: formData.sectionImage,
      imagePreview:
        (keepSectionImage
          ? formData.sectionImagePreview ||
        updated[editingSectionIndex]?.imagePreview ||
        updated[editingSectionIndex]?.image ||
        null
          : null),
      hasImage: keepSectionImage
        ? Boolean(formData.sectionImage || updated[editingSectionIndex]?.image)
        : false,
      image: keepSectionImage
        ? formData.sectionImage
          ? null
          : updated[editingSectionIndex]?.image || null
        : null,
      cloudinary_public_id: keepSectionImage
        ? formData.sectionImage
          ? null
          : updated[editingSectionIndex]?.cloudinary_public_id || null
        : null,
    };

    setFormData({
      ...formData,
      sections: updated,
      sectionContentEn: "",
      sectionContentBn: "",
      sectionImage: null,
      sectionImagePreview: null,
    });
    setEditingSectionIndex(null);
  };

  const cancelEditSection = () => {
    setFormData({
      ...formData,
      sectionContentEn: "",
      sectionContentBn: "",
      sectionImage: null,
      sectionImagePreview: null,
    });
    setEditingSectionIndex(null);
  };

  const validateForm = () => {
    if (!formData.appeal.trim()) {
      toast.error("Appeal name is required");
      return false;
    }

    if (!formData.slug.trim()) {
      toast.error("Slug is required");
      return false;
    }

    const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
    if (!slugRegex.test(formData.slug.toLowerCase())) {
      toast.error(
        "Slug must be in kebab-case format (lowercase letters, numbers, and hyphens only)",
      );
      return false;
    }

    if (!formData.titleEn.trim() || !formData.titleBn.trim()) {
      toast.error("Please fill both English and Bengali titles");
      return false;
    }

    if (!formData.existingImage && !formData.selectedImage) {
      toast.error("Please select or keep an appeal image");
      return false;
    }

    if (formData.sections.length === 0) {
      toast.error("Please add at least one section");
      return false;
    }

    return true;
  };

  const handleUpdateAppeal = async (e) => {
    e.preventDefault();

    if (!validateForm() || !appeal) return;

    try {
      setLoading(true);

      const formDataObj = new FormData();
      formDataObj.append("appeal", formData.appeal);
      formDataObj.append("slug", formData.slug.toLowerCase().trim());
      formDataObj.append(
        "title",
        JSON.stringify({
          en: formData.titleEn,
          bn: formData.titleBn,
        }),
      );

      const sectionsPayload = formData.sections.map(
        ({ imageFile, imagePreview, ...section }) => ({
          ...section,
          image: section.image || null,
          cloudinary_public_id: section.cloudinary_public_id || null,
          hasImage: Boolean(imageFile),
        }),
      );

      formDataObj.append("sections", JSON.stringify(sectionsPayload));

      if (formData.selectedImage) {
        formDataObj.append("image", formData.selectedImage);
      }

      formData.sections
        .filter((section) => section.type === "paragraph" && section.imageFile)
        .forEach((section) => {
          formDataObj.append("sectionImages", section.imageFile);
        });

      await appealService.update(appeal.slug, formDataObj);
      toast.success("Appeal updated successfully!");
      onSuccess();
      onClose();
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to update appeal";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!appeal) return null;

  const renderSectionImageField = () => (
    <div>
      <label className="form-label">Optional Image for Paragraph</label>
      <input
        type="file"
        accept="image/*"
        onChange={handleSectionImageChange}
        className="input-field"
      />
      <p className="text-xs text-gray-500 mt-1">
        You can attach one small image to the left side of the paragraph.
      </p>
      {formData.sectionImagePreview && (
        <div className="mt-3 flex items-center gap-3">
          <img
            src={formData.sectionImagePreview}
            alt="Paragraph preview"
            className="w-40 h-24 object-cover rounded-lg border border-gray-200"
          />
          <Button type="button" variant="secondary" size="sm" onClick={clearSectionImage}>
            Remove
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      title="Edit Appeal"
      onClose={onClose}
      size="xl"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={loading || parentLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateAppeal}
            loading={loading || parentLoading}
          >
            {loading || parentLoading ? "Updating..." : "Update Appeal"}
          </Button>
        </>
      }
    >
      <form onSubmit={handleUpdateAppeal} className="space-y-6">
        {/* Appeal Name and Slug */}
        <div className="space-y-4">
          <Input
            label="Appeal Name"
            value={formData.appeal}
            onChange={(e) =>
              setFormData({ ...formData, appeal: e.target.value })
            }
            required
          />

          <Input
            label="Slug"
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            placeholder="e.g., the-flood-of-bangladesh"
            required
            helperText="Lowercase letters, numbers, and hyphens only (kebab-case)"
          />
        </div>

        {/* Titles */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Title (English)"
            value={formData.titleEn}
            onChange={(e) =>
              setFormData({ ...formData, titleEn: e.target.value })
            }
            required
          />
          <Input
            label="Title (Bengali)"
            value={formData.titleBn}
            onChange={(e) =>
              setFormData({ ...formData, titleBn: e.target.value })
            }
            required
          />
        </div>

        {/* Image */}
        <div>
          <h3 className="subsection-title">Appeal Image</h3>
          {formData.existingImage && !formData.selectedImage && (
            <div className="mb-4">
              <img
                src={formData.existingImage}
                alt="Current"
                className="w-full h-48 object-cover rounded-lg"
              />
              <p className="text-xs text-gray-500 mt-2">Current image</p>
            </div>
          )}

          {formData.selectedImage && (
            <div className="mb-4">
              <img
                src={URL.createObjectURL(formData.selectedImage)}
                alt="New"
                className="w-full h-48 object-cover rounded-lg"
              />
              <p className="text-xs text-gray-500 mt-2">New image</p>
            </div>
          )}

          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="input-field"
          />
          <p className="text-xs text-gray-500 mt-1">
            Upload a new image to replace the current one
          </p>
        </div>

        {/* Sections */}
        <div>
          <h3 className="subsection-title">Content Sections</h3>

          {editingSectionIndex === null ? (
            <div className="space-y-3">
              <select
                value={formData.sectionType}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    sectionType: e.target.value,
                    ...(e.target.value !== "paragraph"
                      ? { sectionImage: null, sectionImagePreview: null }
                      : {}),
                  })
                }
                className="input-field"
              >
                <option value="heading">Heading</option>
                <option value="paragraph">Paragraph</option>
                <option value="highlight">Highlight</option>
                <option value="quote">Quote</option>
              </select>

              {formData.sectionType === "paragraph" && renderSectionImageField()}

              <div>
                <label className="form-label">Content (English)</label>
                <RichTextEditor
                  value={formData.sectionContentEn}
                  onChange={(content) =>
                    setFormData({ ...formData, sectionContentEn: content })
                  }
                  placeholder="Content in English with formatting and links..."
                />
              </div>

              <div>
                <label className="form-label">Content (Bengali)</label>
                <RichTextEditor
                  value={formData.sectionContentBn}
                  onChange={(content) =>
                    setFormData({ ...formData, sectionContentBn: content })
                  }
                  placeholder="বিষয়বস্তু বাংলায় ফরম্যাটিং এবং লিঙ্ক সহ..."
                />
              </div>

              <Input
                label="Section Order"
                type="number"
                value={formData.sectionOrder}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    sectionOrder: parseInt(e.target.value),
                  })
                }
                min="1"
              />

              <Button type="button" variant="secondary" onClick={addSection}>
                Add Section
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <select
                value={formData.sectionType}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    sectionType: e.target.value,
                    ...(e.target.value !== "paragraph"
                      ? { sectionImage: null, sectionImagePreview: null }
                      : {}),
                  })
                }
                className="input-field"
              >
                <option value="heading">Heading</option>
                <option value="paragraph">Paragraph</option>
                <option value="highlight">Highlight</option>
                <option value="quote">Quote</option>
              </select>

              {formData.sectionType === "paragraph" && renderSectionImageField()}

              <div>
                <label className="form-label">Content (English)</label>
                <RichTextEditor
                  value={formData.sectionContentEn}
                  onChange={(content) =>
                    setFormData({ ...formData, sectionContentEn: content })
                  }
                  placeholder="Content in English with formatting and links..."
                />
              </div>

              <div>
                <label className="form-label">Content (Bengali)</label>
                <RichTextEditor
                  value={formData.sectionContentBn}
                  onChange={(content) =>
                    setFormData({ ...formData, sectionContentBn: content })
                  }
                  placeholder="বিষয়বস্তু বাংলায় ফরম্যাটিং এবং লিঙ্ক সহ..."
                />
              </div>

              <Input
                label="Section Order"
                type="number"
                value={formData.sectionOrder}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    sectionOrder: parseInt(e.target.value),
                  })
                }
                min="1"
              />

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={saveEditSection}
                >
                  Save Edit
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={cancelEditSection}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {formData.sections.length > 0 && (
            <div className="mt-4 space-y-2">
              {formData.sections.map((section, index) => (
                <div key={index} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded mr-2">
                        {section.type}
                      </span>
                      <span className="text-xs text-gray-500">
                        Order: {section.order}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => startEditSection(index)}
                        className="text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => removeSection(index)}
                        className="text-red-600 hover:text-red-800 px-2 py-1 rounded hover:bg-red-50"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-800 mb-1">
                    <strong>EN:</strong>{" "}
                    <span
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{
                        __html: section.content.en
                          .substring(0, 100)
                          .concat(section.content.en.length > 100 ? "..." : ""),
                      }}
                    />
                  </p>
                  <p className="text-sm text-gray-800">
                    <strong>BN:</strong>{" "}
                    <span
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{
                        __html: section.content.bn
                          .substring(0, 100)
                          .concat(section.content.bn.length > 100 ? "..." : ""),
                      }}
                    />
                  </p>
                  {(section.image || section.imagePreview) && (
                    <div className="mt-3">
                      <img
                        src={section.image || section.imagePreview}
                        alt="Section"
                        className="w-40 h-24 object-cover rounded-lg border border-gray-200"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default EditAppealModal;
